import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kinky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-950 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transform hover:-translate-y-0.5 active:scale-95",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-r from-kinky-600 to-kinky-700 hover:from-kinky-500 hover:to-kinky-600 text-white font-kinky font-semibold shadow-lg hover:shadow-kinky-400/30 hover:animate-kinky-glow",
        kinky:
          "bg-gradient-to-r from-kinky-600 to-kinky-700 hover:from-kinky-500 hover:to-kinky-600 text-white font-kinky font-semibold shadow-lg hover:shadow-kinky-400/30 hover:animate-kinky-glow",
        passion:
          "bg-gradient-to-r from-passion-600 to-passion-700 hover:from-passion-500 hover:to-passion-600 text-white shadow-lg hover:shadow-passion-500/25 hover:animate-sensual-pulse",
        sensual:
          "bg-gradient-to-r from-sensual-600 to-sensual-700 hover:from-sensual-500 hover:to-sensual-600 text-white shadow-lg hover:shadow-sensual-500/25",
        artistic:
          "bg-gradient-to-r from-artistic-600 to-artistic-700 hover:from-artistic-500 hover:to-artistic-600 text-white font-elegant shadow-lg hover:shadow-artistic-500/25 hover:animate-artistic-bloom",
        luxury:
          "bg-gradient-to-r from-luxury-600 to-luxury-700 hover:from-luxury-500 hover:to-luxury-600 text-dark-900 font-fashion font-semibold shadow-lg hover:shadow-luxury-500/25 hover:animate-luxury-shimmer",
        destructive:
          "bg-gradient-to-r from-kinky-600 to-kinky-800 hover:from-kinky-500 hover:to-kinky-700 text-white shadow-lg hover:shadow-kinky-500/30",
        outline:
          "border border-kinky-600/40 bg-dark-800 text-white shadow-sm hover:bg-dark-700 hover:border-kinky-500/60 active:bg-dark-600",
        secondary:
          "bg-dark-800 text-white shadow-sm hover:bg-dark-700 active:bg-dark-600 border border-kinky-600/30 hover:border-kinky-500/50",
        ghost:
          "text-white hover:bg-kinky-950/20 active:bg-kinky-900/30 hover:text-kinky-300",
        link: 
          "text-kinky-400 underline-offset-4 hover:underline hover:text-kinky-300 font-medium",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-lg px-6 text-base",
        xl: "h-14 rounded-lg px-8 text-lg font-semibold",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
        "icon-xl": "h-14 w-14",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white/80" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <span className="animate-pulse">Loading...</span>
          </>
        ) : children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }